import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    Controls,
    Background,
    MiniMap,
    Panel,
    MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import './FlowBuilder.css';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { initialNodes, nodeTypes } from './nodes';
import { validateFlow } from './utils/validation';
import { serializeFlow } from './utils/serialization';
import PropertiesPanel from './components/PropertiesPanel';

const defaultViewport = { x: 0, y: 0, zoom: 1.5 };

const FlowBuilderPage = ({ token: propToken, business_id: propBusinessId }) => {
    // 1. Resolve Credentials (Props or LocalStorage)
    const [token, setToken] = useState(propToken || localStorage.getItem('token'));
    const [businessId, setBusinessId] = useState(propBusinessId || localStorage.getItem('businessId'));

    // Check for prop updates (e.g. if App re-renders with valid props later)
    useEffect(() => {
        if (propToken) setToken(propToken);
        if (propBusinessId) setBusinessId(propBusinessId);
    }, [propToken, propBusinessId]);

    // Add debug log to verify props on mount/update
    useEffect(() => {
        console.log("FlowBuilderPage Mounted/Updated. Credentials:", { token, businessId });
    }, [token, businessId]);

    // View Mode: 'list' or 'builder'
    const [viewMode, setViewMode] = useState('list');
    const [workflows, setWorkflows] = useState([]);
    const [brandId, setBrandId] = useState(null);
    const [loading, setLoading] = useState(false);

    // Builder State
    const reactFlowWrapper = useRef(null);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [selectedElement, setSelectedElement] = useState(null);

    // Flow Metadata State
    const [flowId, setFlowId] = useState(null); // ID of flow currently being edited
    const [flowName, setFlowName] = useState('New Flow');
    const [isActive, setIsActive] = useState(false);
    const [triggers, setTriggers] = useState([]);

    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.staging.waitwhiz.in';

    // 2. Fetch Business Info (Brand ID) on mount
    useEffect(() => {
        const fetchBusinessInfo = async () => {
            // Removing strict check for logging purposes, check inside
            if (!token) {
                console.warn("Skipping fetchBusinessInfo: Missing token", { hasToken: !!token });
                return;
            }
            try {
                // Try fetching with business_id if available, or just token if not (assuming backend handles it)
                let url = `${apiUrl}/business/business_info?auth_token=${token}`;
                if (businessId) {
                    url += `&business_id=${businessId}`;
                }

                console.log("Fetching Business Info...", url);
                const response = await fetch(url, { credentials: 'include' });
                if (!response.ok) {
                    const txt = await response.text();
                    throw new Error(`Failed to fetch business info: ${response.status} ${txt}`);
                }
                const data = await response.json();
                console.log("Business Info Loaded:", data);

                // Extract Brand ID
                if (data.data && data.data.brand_id) {
                    setBrandId(data.data.brand_id);
                } else {
                    console.error("brand_id missing in response", data);
                }

                // If we didn't have businessId but API returned it, update it (if applicable)
                if (!businessId && data.data && data.data.business_id) {
                    setBusinessId(data.data.business_id);
                }

            } catch (error) {
                console.error("Error fetching business info:", error);
            }
        };
        fetchBusinessInfo();
    }, [businessId, token, apiUrl]);

    // 2. Fetch Workflows when in List mode and Brand ID is available
    useEffect(() => {
        if (viewMode === 'list' && brandId && token) {
            fetchWorkflows();
        } else {
            console.log("Skipping fetchWorkflows. Conditions:", { viewMode, hasBrandId: !!brandId, hasToken: !!token });
        }
    }, [viewMode, brandId, token]);

    const fetchWorkflows = async () => {
        setLoading(true);
        try {
            const url = `${apiUrl}/business/workflows?brand_id=${brandId}&auth_token=${token}`;
            console.log("Fetching Workflows...", url);
            const response = await fetch(url, {
                headers: { 'Referer': 'https://app.staging.waitwhiz.in' },
                credentials: 'include'
            });
            if (!response.ok) throw new Error("Failed to fetch workflows");
            const data = await response.json();
            console.log("Workflows Loaded:", data);

            // Handle various likely response shapes
            let items = [];
            if (Array.isArray(data)) {
                items = data;
            } else if (data.data && Array.isArray(data.data)) {
                items = data.data;
            } else if (data.items && Array.isArray(data.items)) {
                items = data.items;
            }

            setWorkflows(items);
        } catch (error) {
            console.error("Error fetching workflows:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setFlowId(null);
        setFlowName('New Flow');
        setIsActive(false);
        setTriggers([]);
        setNodes(initialNodes);
        setEdges([]);
        setSelectedElement(null);
        setViewMode('builder');
    };

    const handleEdit = async (workflow) => {
        setLoading(true);
        try {
            const url = `${apiUrl}/business/workflows/${workflow.id}?auth_token=${token}`;
            console.log("Editing Workflow...", url);
            // Fetch full details
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) throw new Error("Failed to fetch workflow details");
            const responseData = await response.json();

            // Handle { success: true, data: { ... } } wrapper
            const data = responseData.data || responseData;
            console.log("Loaded Workflow Data:", data);

            setFlowId(data.id);
            setFlowName(data.name);
            setIsActive(data.is_active);
            setTriggers(data.triggers || []);

            // -----------------------------------------------------
            // 4. Auto-Layout Logic (Hierarchical / Tree-like)
            // -----------------------------------------------------
            const loadedEdgesRaw = data.edges || [];

            // Map raw nodes first to normalized config structure
            let rawNodes = (data.nodes || []).map(n => {
                let config = n.config || {};

                // Flatten create_reservation structure
                if (n.type === 'create_reservation' && config.vars) {
                    // Extract vars and spread them to root config for UI
                    const { vars, ...rest } = config;
                    config = {
                        ...rest,
                        ...vars, // date, time, name, etc.
                        reservation_date: vars.date,
                        reservation_time: vars.time,
                        reservation_name: vars.name,
                        reservation_number: vars.number,
                        reservation_country_code: vars.country_code,
                        reservation_pax: vars.no_of_people,
                        reservation_business_id: vars.business_id,
                        reservation_remark: vars.remark
                    };
                }

                // Flatten send_form structure if nested
                if (n.type === 'send_form' && config.form) {
                    config = {
                        ...config.form, // Spread form first (defaults/legacy)
                        ...config,      // Spread root config second (overrides)
                    };
                    delete config.form; // Remove nested object for UI
                }

                // Flatten send_payment_link structure
                if (n.type === 'send_payment_link' && config.vars) {
                    const vars = config.vars || {};
                    const payConfig = vars.payment_config || {};
                    const payInfo = vars.payment_info || {};

                    config = {
                        ...config,
                        ...payConfig, // body_text, catalog_id...
                        ...payInfo,   // currency, tax_rate, total_amount, product_items...
                        // Flatten legacy or nested gateway if needed, but our UI expects 'payment_gateway' as string 'razorpay' etc?
                        // Actually UI expects `payment_gateway` scalar.
                        // Backend has `payment_gateway: { type: 'razorpay' ... }` inside payConfig.
                    };

                    if (payConfig.payment_gateway && typeof payConfig.payment_gateway === 'object') {
                        config.payment_gateway = payConfig.payment_gateway.type; // Extract 'razorpay'
                    }

                    // Remove vars wrapper to avoid confusion
                    const { vars: _, ...rest } = config;
                    config = rest;
                }

                return {
                    id: n.id,
                    type: n.type,
                    data: { label: n.type, config },
                    position: n.position || null // Keep existing if present, else null
                };
            });

            // Ensure visual start node exists in raw list if not present
            if (!rawNodes.find(n => n.type === 'start')) {
                rawNodes.unshift({
                    id: 'start_node',
                    type: 'start',
                    data: { label: 'Start' },
                    position: { x: 400, y: 50 } // Default top center
                });
            }

            // Build Adjacency List
            const adj = {};
            const incomingCounts = {};
            rawNodes.forEach(n => {
                adj[n.id] = [];
                incomingCounts[n.id] = 0;
            });

            // Parse Edges to build graph
            // Note: We need temporary edges to build layout, even if we reconstruct loadedEdges later
            const edgeList = loadedEdgesRaw.map(e => ({ source: e.from_id, target: e.to_id }));

            // Add virtual edge from Start Visual to Start Logical
            if (data.start_node_id) {
                edgeList.push({ source: 'start_node', target: data.start_node_id });
            }

            edgeList.forEach(e => {
                if (adj[e.source]) adj[e.source].push(e.target);
                if (incomingCounts[e.target] !== undefined) incomingCounts[e.target]++;
            });

            // BFS for Levels
            const levels = {}; // id -> level
            const nodesByLevel = {}; // level -> [id]
            const queue = [{ id: 'start_node', level: 0 }];
            const visited = new Set(['start_node']);

            while (queue.length > 0) {
                const { id, level } = queue.shift();
                levels[id] = level;

                if (!nodesByLevel[level]) nodesByLevel[level] = [];
                nodesByLevel[level].push(id);

                const neighbors = adj[id] || [];
                neighbors.forEach(nid => {
                    if (!visited.has(nid)) {
                        visited.add(nid);
                        queue.push({ id: nid, level: level + 1 });
                    }
                });
            }

            // Handle disconnected nodes (assign to level 0 or deep)
            rawNodes.forEach(n => {
                if (!visited.has(n.id)) {
                    // Just put them at level 1 for now or skip auto-layout for them
                    const level = 1;
                    if (!nodesByLevel[level]) nodesByLevel[level] = [];
                    nodesByLevel[level].push(n.id);
                }
            });

            // Assign Positions
            const LEVEL_HEIGHT = 150;
            const NODE_WIDTH = 250;

            const loadedNodes = rawNodes.map(n => {
                // If position exists and is valid, user might have dragged it. 
                // However, request says "ensure this is loaded... show first node on top...".
                // Implies we force layout or prefer it. Let's auto-layout if position is missing OR simple 0,0.
                let pos = n.position;

                if (!pos || (pos.x === 0 && pos.y === 0)) {
                    const level = levels[n.id] !== undefined ? levels[n.id] : 1;
                    const indexInLevel = nodesByLevel[level].indexOf(n.id);
                    const totalInLevel = nodesByLevel[level].length;

                    // Center the row
                    const totalWidth = totalInLevel * NODE_WIDTH;
                    const startX = 400 - (totalWidth / 2); // Center around x=400

                    pos = {
                        x: startX + (indexInLevel * NODE_WIDTH),
                        y: 50 + (level * LEVEL_HEIGHT)
                    };
                }

                return {
                    ...n,
                    position: pos
                };
            });

            const loadedEdges = (data.edges || []).map(e => ({
                id: uuidv4(),
                source: e.from_id, // Map from_id -> source
                target: e.to_id,   // Map to_id -> target
                type: 'default',
                markerEnd: { type: MarkerType.ArrowClosed },
                data: { condition: e.condition }
            }));

            // Reconnect Start Node Visual to the real start of the flow
            if (data.start_node_id) {
                // Ensure we don't duplicate if for some reason it exists
                const existingEdge = loadedEdges.find(e => e.source === 'start_node' && e.target === data.start_node_id);
                if (!existingEdge) {
                    loadedEdges.push({
                        id: 'start-edge',
                        source: 'start_node',
                        target: data.start_node_id,
                        type: 'default',
                        markerEnd: { type: MarkerType.ArrowClosed },
                        data: { condition: { type: 'always' } }
                    });
                }
            }

            setNodes(loadedNodes);
            setEdges(loadedEdges); // Fix: setEdges, not loadedEdges
            setViewMode('builder');
        } catch (error) {
            console.error("Error loading workflow:", error);
            alert("Failed to load workflow.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this flow?")) return;
        try {
            const url = `${apiUrl}/business/workflows/${id}?auth_token=${token}`;
            const response = await fetch(url, { method: 'DELETE', credentials: 'include' });
            if (!response.ok) throw new Error("Failed to delete");
            fetchWorkflows(); // Refresh
        } catch (error) {
            alert(error.message);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${!currentStatus ? 'activate' : 'deactivate'} this flow?`)) return;
        try {
            const url = `${apiUrl}/business/workflows/${id}/status?auth_token=${token}`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: !currentStatus }),
                credentials: 'include'
            });
            if (!response.ok) throw new Error("Failed to update status");
            fetchWorkflows(); // Refresh
        } catch (error) {
            alert(error.message);
        }
    };

    // Drag and Drop
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();
            const type = event.dataTransfer.getData('application/reactflow');
            if (typeof type === 'undefined' || !type) return;

            const position = reactFlowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            // Human readable id generation
            const existingTypeCount = nodes.filter(n => n.type === type).length;
            let humanId;
            if (type === 'start') {
                if (existingTypeCount > 0) {
                    alert("Only one start node allowed");
                    return;
                }
                humanId = 'start_node';
            } else if (type === 'end') {
                humanId = `end_node_${existingTypeCount + 1}`;
            } else {
                humanId = `${type}_${existingTypeCount + 1}`;
            }

            // Default Config Initialization
            let defaultConfig = {};
            if (type === 'create_reservation') {
                defaultConfig = {
                    reservation_date: '2025-12-31',
                    reservation_time: '20:30',
                    reservation_name: '{name}',
                    reservation_number: '{parsed.national_number}',
                    reservation_country_code: '{parsed.country_code}',
                    reservation_pax: '{form_data.data.no_of_people_s}',
                    reservation_business_id: '2',
                    reservation_remark: '',
                    output_payload_var: 'reserv_data'
                };
            }

            const newNode = {
                id: humanId,
                type,
                position,
                data: { label: `${type} node`, config: defaultConfig },
            };

            setNodes((nds) => nds.concat(newNode));
        },
        [reactFlowInstance, setNodes, nodes]
    );

    const onConnect = useCallback(
        (params) => {
            const sourceNode = nodes.find(n => n.id === params.source);
            let defaultConditionStart = 'always';
            if (sourceNode && (sourceNode.type === 'wait_event' || sourceNode.type === 'send_form')) {
                defaultConditionStart = 'event';
            }

            const newEdge = {
                ...params,
                id: uuidv4(),
                type: 'default', // or custom edge type
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                },
                data: {
                    condition: { type: defaultConditionStart }
                }
            };
            setEdges((eds) => addEdge(newEdge, eds));
            setSelectedElement(newEdge);
        },
        [setEdges, setSelectedElement, nodes]
    );

    const handleSave = async () => {
        const flowData = {
            name: flowName,
            is_active: isActive,
            triggers,
            nodes,
            edges
        };

        if (flowId) {
            flowData.id = flowId; // Include ID for updates
        }

        const validationResult = validateFlow(flowData);
        if (!validationResult.isValid) {
            alert(`Cannot save: ${validationResult.error}`);
            return;
        }

        const jsonOutput = serializeFlow(flowData);
        if (flowId) jsonOutput.id = flowId;

        try {
            const saveUrl = `${apiUrl}/business/workflows?brand_id=${brandId}&auth_token=${token}`;
            console.log("Saving Flow to:", saveUrl, jsonOutput);

            const response = await fetch(saveUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Referer': 'https://app.staging.waitwhiz.in'
                },
                body: JSON.stringify(jsonOutput),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to save flow: ${response.status} ${errorData.detail || response.statusText}`);
            }

            const result = await response.json();
            alert(`Flow saved successfully!`);
            setViewMode('list'); // Return to list
            fetchWorkflows(); // Refresh list
        } catch (error) {
            console.error("Save Error:", error);
            alert(`Error saving flow: ${error.message}`);
        }
    };

    if (viewMode === 'list') {
        return (
            <div className="wf-container" style={{ padding: '20px', display: 'block', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                        <h2 style={{ color: 'white', margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Workflows</h2>
                        <p style={{ color: 'var(--wf-text-secondary)', margin: '5px 0 0', fontSize: '0.9rem' }}>Manage your automation flows</p>
                    </div>
                    <button className="wf-btn wf-btn-primary" onClick={handleCreateNew}>
                        <span style={{ marginRight: '8px' }}>+</span>
                        New Flow
                    </button>
                </div>

                {loading && workflows.length === 0 ? (
                    <div style={{ color: 'var(--wf-text)', textAlign: 'center', marginTop: '50px' }}>Loading workflows...</div>
                ) : (
                    <div className="wf-grid">
                        {workflows.map(wf => (
                            <div key={wf.id} className="wf-card">
                                <div>
                                    <div className="wf-card-header">
                                        <div className="wf-card-title" title={wf.name}>{wf.name}</div>
                                        <div className={`wf-badge ${wf.is_active ? 'wf-badge-active' : 'wf-badge-inactive'}`}>
                                            {wf.is_active ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <div className="wf-card-meta">
                                        ID: {wf.id}
                                    </div>
                                    <div className="wf-card-meta">
                                        Last Updated: {new Date(wf.updated_at).toLocaleDateString()}
                                    </div>
                                </div>

                                <div>
                                    <div className="wf-card-status">
                                        <span style={{ fontSize: '0.85rem', color: 'var(--wf-text-secondary)' }}>Status:</span>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={wf.is_active}
                                                onChange={() => handleToggleStatus(wf.id, wf.is_active)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>

                                    <div className="wf-card-actions">
                                        <button
                                            className="wf-btn"
                                            style={{ flex: 1, background: 'rgba(255,255,255,0.1)' }}
                                            onClick={() => handleEdit(wf)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="wf-btn"
                                            style={{ flex: 1, background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}
                                            onClick={() => handleDelete(wf.id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {workflows.length === 0 && !loading && (
                            <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--wf-text-secondary)', padding: '40px' }}>
                                No workflows found. Create one to get started.
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="wf-container">
            <div style={{ position: 'absolute', top: '15px', left: '15px', zIndex: 20 }}>
                <button className="wf-back-btn" onClick={() => {
                    setViewMode('list');
                    fetchWorkflows(); // Refresh list on return
                }}>
                    <span>&larr;</span> Back to Workflows
                </button>
            </div>
            <Header
                flowName={flowName}
                setFlowName={setFlowName}
                isActive={isActive}
                setIsActive={setIsActive}
                onSave={handleSave}
            />

            <div className="wf-main">
                <Sidebar
                    triggers={triggers}
                    setTriggers={setTriggers}
                />

                <div className="wf-canvas" ref={reactFlowWrapper}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onInit={setReactFlowInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onNodeClick={(_, node) => setSelectedElement(node)}
                        onEdgeClick={(_, edge) => setSelectedElement(edge)}
                        onPaneClick={() => setSelectedElement(null)}
                        nodeTypes={nodeTypes}
                        defaultViewport={defaultViewport}
                        fitView
                        snapToGrid={true}
                        snapGrid={[15, 15]}
                    >
                        <Controls />
                        <MiniMap style={{ height: 100 }} zoomable pannable />
                        <Background color="#aaa" gap={16} />
                    </ReactFlow>
                </div>

                {selectedElement && (
                    <PropertiesPanel
                        selectedElement={selectedElement}
                        onChange={(id, newData) => {
                            // Check if it's a node or edge
                            const isNode = nodes.some(n => n.id === id);

                            if (isNode) {
                                setNodes((nds) => nds.map((node) => {
                                    if (node.id === id) {
                                        const updatedNode = { ...node, data: newData };
                                        setSelectedElement(updatedNode);
                                        return updatedNode;
                                    }
                                    return node;
                                }));
                            } else {
                                setEdges((eds) => eds.map((edge) => {
                                    if (edge.id === id) {
                                        const updatedEdge = { ...edge, data: newData };
                                        setSelectedElement(updatedEdge);
                                        return updatedEdge;
                                    }
                                    return edge;
                                }));
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default () => (
    <ReactFlowProvider>
        <FlowBuilderPage />
    </ReactFlowProvider>
);
